import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import LogRocket from 'logrocket';
import { Intercom } from 'ng-intercom';
import { NgxPendoService } from 'ngx-pendo';
import { Subscription } from 'rxjs';
import { delay, filter } from 'rxjs/operators';

import {
  LayoutConfigService,
  SplashScreenService,
  TranslationService,
} from '@app/core/_base/layout';
import { VersionCheckService } from '@app/core/_base/layout/services/version-check.service';
import { locale as chLang } from '@app/core/_config/i18n/ch';
import { locale as deLang } from '@app/core/_config/i18n/de';
import { locale as enLang } from '@app/core/_config/i18n/en';
import { locale as esLang } from '@app/core/_config/i18n/es';
import { locale as frLang } from '@app/core/_config/i18n/fr';
import { locale as jpLang } from '@app/core/_config/i18n/jp';
import { AuthService } from '@app/core/auth';
import { environment as envService } from '@app/env-service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { ServiceBellService } from '@app/shared/services/service-bell.service';
import { UpscopeService } from '@app/shared/services/upscope.service';
import { environment } from '@env/environment';
import { router } from '@uni/router/router';

//

let recordingURL;
LogRocket.getSessionURL((sessionURL) => {
  recordingURL = sessionURL;
});

//

@Component({
  selector: 'body[kt-root]', // eslint-disable-line @angular-eslint/component-selector
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribe :Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  clientConfig :any = envService.clientConfig;
  loader :boolean;
  title = 'Luminous';

  @ViewChild('svgOutlet', { static: true }) svgOutlet :ElementRef;

  constructor(
    private _feature :FeatureToggleService,
    private _intercom :Intercom,
    private _ServiceBell :ServiceBellService,
    private _Upscope :UpscopeService,
    private authService :AuthService,
    private layoutConfigService :LayoutConfigService,
    private router :Router,
    private splashScreenService :SplashScreenService,
    private titleSvc :Title,
    private translationService :TranslationService,
    private versionCheckService :VersionCheckService,
    protected ngxPendoService :NgxPendoService,
    public sanitizer :DomSanitizer,
  ) {
    // register translations
    this.translationService.loadTranslations(
      enLang,
      chLang,
      esLang,
      jpLang,
      deLang,
      frLang,
    );

    this._intercom.boot({
      app_id: 'b345by52',
      custom_launcher_selector: '#luminous-intercom-toggle',
      hide_default_launcher: true,
      widget: {
        activator: '#intercom',
      },
    });

    this._ServiceBell.init();
    this._Upscope.init();
  }

  ngOnInit() {
    // set up interop with other routers
    router.setAngularCallback((commands, extras) => {
      this.router.navigate(commands, extras);
    });

    // enable/disable loader
    this.loader = this.layoutConfigService.getConfig('loader.enabled');

    this.titleSvc.setTitle(envService.getHumanSummary());

    this.unsubscribe = this.getScrollPosition();

    this.versionCheckService.initVersionCheck(envService.versionCheckURL);

    // add class to body
    const bodyTag = document.body;
    bodyTag.classList.add(envService.clientConfig.className);

    // if frame=1 is in the query params then add iframe to the body class.
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('frame')) {
      bodyTag.classList.add('iframe');
    }

    // Logrocket
    if (environment.production) {
      LogRocket.init('jmsum4/luminous-v1');
    }
    // End Logrocket
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  private getScrollPosition() :any {
    this.router.events
      .pipe(
        filter((e) :e is Scroll => e instanceof Scroll),
        delay(1),
      )
      .subscribe((event) => {
        if (event.routerEvent instanceof NavigationEnd) {
          // hide splash screen
          this.splashScreenService.hide();

          // scroll to previous state on every route change
          if (event.position) {
            window.scrollTo(0, event.position[1]);
          } else {
            window.scrollTo(0, 0);
          }
        }
      });
  }
}
